import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  approveQualityPolicyDetail,
  approveQualityPolicyPlan,
  approveQualityPolicyReport,
  closeQualityPolicyCycle,
  createQualityPolicyCycle,
  deleteQualityPolicyCycle,
  fetchQualityPolicyCycle,
  fetchQualityPolicyCycles,
  reopenQualityPolicyCycle,
  revokeQualityPolicyDetail,
  revokeQualityPolicyPlan,
  revokeQualityPolicyReport,
  updateQualityPolicyDetail,
  updateQualityPolicyPlan,
  updateQualityPolicyReport,
  downloadQualityPolicyPlanPdf,
  downloadQualityPolicyDetailPdf,
  downloadQualityPolicyReportPdf,
} from '../../api/qualityPolicy';
import { notifyError, reportError } from '../../utils/notify';
import { useCompanyStore } from '../../store/companyStore';
import { canConfirmInspection } from '../../types/companyRole';
import {
  APPROVAL_TARGET_QUALITY_POLICY_DETAIL,
  APPROVAL_TARGET_QUALITY_POLICY_PLAN,
  APPROVAL_TARGET_QUALITY_POLICY_REPORT,
} from '../../types/approval';
import {
  QualityDetailForm,
  QualityPlanForm,
  QualityPolicyCycle,
  QualityReportForm,
} from '../../types/qualityPolicy';
import {
  parseDetailForm,
  parsePlanForm,
  parseReportForm,
  syncResultsFromObjectives,
  syncTasksFromObjectives,
} from '../../utils/qualityPolicyForms';
import ApprovalCancelButton from '../approval/ApprovalCancelButton';
import ApprovalRequestModal from '../approval/ApprovalRequestModal';
import QualityDetailFormView from './QualityDetailFormView';
import QualityPlanFormView from './QualityPlanFormView';
import QualityReportFormView from './QualityReportFormView';
import '../product/NonconformanceReportPanel.css';
import '../product/InspectionFormSheet.css';
import './QualityPolicyPanel.css';

type DocKey = 'plan' | 'detail' | 'report';

const DOC_META: Record<DocKey, { label: string; step: number }> = {
  plan: { label: '추진 계획서', step: 1 },
  detail: { label: '세부 계획서', step: 2 },
  report: { label: '보고서', step: 3 },
};

const DOC_APPROVAL_TARGET: Record<DocKey, string> = {
  plan: APPROVAL_TARGET_QUALITY_POLICY_PLAN,
  detail: APPROVAL_TARGET_QUALITY_POLICY_DETAIL,
  report: APPROVAL_TARGET_QUALITY_POLICY_REPORT,
};

function statusLabel(status: string | null | undefined, approvalInProgress?: boolean | null) {
  if (approvalInProgress) return '결재 요청 중';
  switch (status) {
    case 'APPROVED':
      return '승인';
    case 'CONFIRMED':
      return '확정';
    case 'DRAFT':
      return '작성중';
    case 'EMPTY':
      return '미작성';
    case 'OPEN':
      return '진행중';
    case 'CLOSED':
      return '마감';
    default:
      return status || '-';
  }
}

function statusClass(status: string | null | undefined, approvalInProgress?: boolean | null) {
  if (approvalInProgress) return 'is-pending';
  switch (status) {
    case 'APPROVED':
      return 'is-approved';
    case 'CONFIRMED':
      return 'is-confirmed';
    case 'DRAFT':
      return 'is-draft';
    case 'CLOSED':
      return 'is-closed';
    case 'OPEN':
      return 'is-open';
    default:
      return 'is-empty';
  }
}

function isApproved(status?: string | null) {
  return (status || '').toUpperCase() === 'APPROVED';
}

function defaultTitle(year: number, doc: DocKey) {
  if (doc === 'plan') return `${year}년 품질방침 추진 계획서`;
  if (doc === 'detail') return `${year}년 품질방침 세부 계획서`;
  return `${year}년 품질방침 보고서`;
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ['작성', authorName],
          ['검토', reviewerName],
          ['승인', approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span style={{ color: value ? '#1f2a37' : '#98a2b3', fontWeight: value ? 600 : 400 }}>
            {value || '-'}
          </span>
        </div>
      ))}
    </div>
  );
}

function QualityPolicyPanel() {
  const role = useCompanyStore((s) => s.role);
  const canApproveDirect = canConfirmInspection(role);
  const [list, setList] = useState<QualityPolicyCycle[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<QualityPolicyCycle | null>(null);
  const [activeDoc, setActiveDoc] = useState<DocKey>('plan');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [error, setError] = useState('');
  const [approvalMessage, setApprovalMessage] = useState('');
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [createYear, setCreateYear] = useState(String(new Date().getFullYear()));
  const [createTitle, setCreateTitle] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const [docTitle, setDocTitle] = useState('');
  const [planForm, setPlanForm] = useState<QualityPlanForm>(() => parsePlanForm(null, new Date().getFullYear()));
  const [detailForm, setDetailForm] = useState<QualityDetailForm>(() =>
    parseDetailForm(null, new Date().getFullYear())
  );
  const [reportForm, setReportForm] = useState<QualityReportForm>(() =>
    parseReportForm(null, new Date().getFullYear())
  );

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await fetchQualityPolicyCycles();
      setList(rows);
      if (selectedId == null && rows.length > 0) {
        setSelectedId(rows[0].cycleId);
      } else if (selectedId != null && !rows.some((r) => r.cycleId === selectedId)) {
        setSelectedId(rows[0]?.cycleId ?? null);
        if (!rows.length) setDetail(null);
      }
    } catch {
      reportError(setError, '품질방침 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    void loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedId == null) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const row = await fetchQualityPolicyCycle(selectedId);
        if (!cancelled) setDetail(row);
      } catch {
        if (!cancelled) reportError(setError, '상세를 불러오지 못했습니다.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  useEffect(() => {
    if (!detail) return;
    const year = detail.cycleYear;
    setPlanForm(parsePlanForm(detail.planContent, year));
    setDetailForm(parseDetailForm(detail.detailContent, year));
    setReportForm(parseReportForm(detail.reportContent, year));
  }, [detail]);

  useEffect(() => {
    if (!detail) return;
    const year = detail.cycleYear;
    if (activeDoc === 'plan') {
      setDocTitle(detail.planTitle || defaultTitle(year, 'plan'));
    } else if (activeDoc === 'detail') {
      setDocTitle(detail.detailTitle || defaultTitle(year, 'detail'));
    } else {
      setDocTitle(detail.reportTitle || defaultTitle(year, 'report'));
    }
  }, [detail, activeDoc]);

  const locked = detail?.status === 'CLOSED';

  const currentDocStatus = useMemo(() => {
    if (!detail) return 'EMPTY';
    if (activeDoc === 'plan') return detail.planStatus;
    if (activeDoc === 'detail') return detail.detailStatus;
    return detail.reportStatus;
  }, [detail, activeDoc]);

  const sectionApproved = isApproved(currentDocStatus);

  const sectionApprovalPending = useMemo(() => {
    if (!detail) return { approvalInProgress: false, pendingApprovalRequestId: null as number | null, canCancelApproval: false };
    if (activeDoc === 'plan') {
      return {
        approvalInProgress: Boolean(detail.planApprovalInProgress),
        pendingApprovalRequestId: detail.planPendingApprovalRequestId ?? null,
        canCancelApproval: Boolean(detail.planCanCancelApproval),
      };
    }
    if (activeDoc === 'detail') {
      return {
        approvalInProgress: Boolean(detail.detailApprovalInProgress),
        pendingApprovalRequestId: detail.detailPendingApprovalRequestId ?? null,
        canCancelApproval: Boolean(detail.detailCanCancelApproval),
      };
    }
    return {
      approvalInProgress: Boolean(detail.reportApprovalInProgress),
      pendingApprovalRequestId: detail.reportPendingApprovalRequestId ?? null,
      canCancelApproval: Boolean(detail.reportCanCancelApproval),
    };
  }, [detail, activeDoc]);

  const sectionCanRevoke = useMemo(() => {
    if (!detail) return false;
    if (activeDoc === 'plan') return Boolean(detail.planCanRevoke);
    if (activeDoc === 'detail') return Boolean(detail.detailCanRevoke);
    return Boolean(detail.reportCanRevoke);
  }, [detail, activeDoc]);

  const sectionStamps = useMemo(() => {
    if (!detail) {
      return { authorName: null, reviewerName: null, approverName: null };
    }
    if (activeDoc === 'plan') {
      return {
        authorName: detail.planAuthorName,
        reviewerName: detail.planReviewerName,
        approverName: detail.planApproverName,
      };
    }
    if (activeDoc === 'detail') {
      return {
        authorName: detail.detailAuthorName,
        reviewerName: detail.detailReviewerName,
        approverName: detail.detailApproverName,
      };
    }
    return {
      authorName: detail.reportAuthorName,
      reviewerName: detail.reportReviewerName,
      approverName: detail.reportApproverName,
    };
  }, [detail, activeDoc]);

  const editLocked = locked || sectionApproved || sectionApprovalPending.approvalInProgress;

  /** 저장(DRAFT) 이후에만 결재/바로승인 */
  const canRequestApproval =
    !locked &&
    !sectionApproved &&
    !sectionApprovalPending.approvalInProgress &&
    (currentDocStatus === 'DRAFT' || currentDocStatus === 'CONFIRMED');

  /** 연도 마감: 계획·세부·보고서 모두 승인 완료 후 */
  const canCloseCycle =
    !!detail &&
    !locked &&
    isApproved(detail.planStatus) &&
    isApproved(detail.detailStatus) &&
    isApproved(detail.reportStatus);

  const stageCards = useMemo(() => {
    if (!detail) return [];
    return (['plan', 'detail', 'report'] as DocKey[]).map((key) => {
      const status =
        key === 'plan'
          ? detail.planStatus
          : key === 'detail'
            ? detail.detailStatus
            : detail.reportStatus;
      const title =
        key === 'plan'
          ? detail.planTitle
          : key === 'detail'
            ? detail.detailTitle
            : detail.reportTitle;
      const approvalInProgress =
        key === 'plan'
          ? detail.planApprovalInProgress
          : key === 'detail'
            ? detail.detailApprovalInProgress
            : detail.reportApprovalInProgress;
      return { key, status, title, approvalInProgress, ...DOC_META[key] };
    });
  }, [detail]);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    const year = Number(createYear);
    if (!Number.isFinite(year)) {
      reportError(setError, '연도를 확인해 주세요.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const created = await createQualityPolicyCycle({
        cycleYear: year,
        title: createTitle.trim() || undefined,
      });
      setShowCreate(false);
      setCreateTitle('');
      await loadList();
      setSelectedId(created.cycleId);
      setDetail(created);
      setActiveDoc('plan');
    } catch {
      reportError(setError, '연도 생성에 실패했습니다. 이미 등록된 연도인지 확인해 주세요.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!detail || editLocked) return;
    const wasEmpty = currentDocStatus === 'EMPTY';
    setSaving(true);
    setError('');
    setApprovalMessage('');
    try {
      const title = docTitle.trim() || defaultTitle(detail.cycleYear, activeDoc);
      const content =
        activeDoc === 'plan'
          ? JSON.stringify(planForm)
          : activeDoc === 'detail'
            ? JSON.stringify(detailForm)
            : JSON.stringify(reportForm);
      const payload = { title, content, status: 'DRAFT' as const };
      const updater =
        activeDoc === 'plan'
          ? updateQualityPolicyPlan
          : activeDoc === 'detail'
            ? updateQualityPolicyDetail
            : updateQualityPolicyReport;
      const updated = await updater(detail.cycleId, payload);
      setDetail(updated);
      await loadList();
      setApprovalMessage(
        wasEmpty
          ? '저장되었습니다. 상단에서 결재 요청하거나(팀장 이상) 바로 승인할 수 있습니다.'
          : '저장되었습니다.'
      );
      window.requestAnimationFrame(() => {
        document
          .getElementById('qpolicy-doc-actions')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    } catch {
      reportError(setError, '문서 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!detail) return;
    if (!canApproveDirect) {
      reportError(setError, '바로 승인은 오너/팀장만 가능합니다.');
      return;
    }
    if (
      !window.confirm(
        `${DOC_META[activeDoc].label}를 바로 승인할까요? 승인 후에는 수정할 수 없습니다.`
      )
    ) {
      return;
    }
    setApproving(true);
    setError('');
    setApprovalMessage('');
    try {
      const approver =
        activeDoc === 'plan'
          ? approveQualityPolicyPlan
          : activeDoc === 'detail'
            ? approveQualityPolicyDetail
            : approveQualityPolicyReport;
      const updated = await approver(detail.cycleId);
      setDetail(updated);
      setApprovalMessage('바로 승인되었습니다.');
      await loadList();
    } catch {
      reportError(setError, '바로 승인에 실패했습니다.');
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    if (!detail) return;
    if (!window.confirm('승인을 철회할까요?\n승인란·검토란이 지워집니다.')) return;
    setApproving(true);
    setError('');
    setApprovalMessage('');
    try {
      const revoker =
        activeDoc === 'plan'
          ? revokeQualityPolicyPlan
          : activeDoc === 'detail'
            ? revokeQualityPolicyDetail
            : revokeQualityPolicyReport;
      const updated = await revoker(detail.cycleId);
      setDetail(updated);
      setApprovalMessage('승인이 철회되었습니다.');
      await loadList();
    } catch {
      reportError(setError, '승인 철회에 실패했습니다.');
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!detail) return;
    try {
      setPdfDownloading(true);
      const name = docTitle.trim() || `${detail.cycleYear}_${activeDoc}`;
      if (activeDoc === 'plan') {
        await downloadQualityPolicyPlanPdf(detail.cycleId, `품질방침계획_${name}.pdf`);
      } else if (activeDoc === 'detail') {
        await downloadQualityPolicyDetailPdf(detail.cycleId, `품질방침세부_${name}.pdf`);
      } else {
        await downloadQualityPolicyReportPdf(detail.cycleId, `품질방침보고_${name}.pdf`);
      }
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.');
    } finally {
      setPdfDownloading(false);
    }
  };


  const handleCloseCycle = async () => {
    if (!detail) return;
    if (!window.confirm(`${detail.cycleYear}년 품질방침을 마감할까요?`)) return;
    setSaving(true);
    try {
      const updated = await closeQualityPolicyCycle(detail.cycleId);
      setDetail(updated);
      await loadList();
    } catch {
      reportError(setError, '마감 처리에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleReopenCycle = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const updated = await reopenQualityPolicyCycle(detail.cycleId);
      setDetail(updated);
      await loadList();
    } catch {
      reportError(setError, '재개 처리에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!detail) return;
    if (!window.confirm(`${detail.cycleYear}년 품질방침을 삭제할까요?`)) return;
    setSaving(true);
    try {
      await deleteQualityPolicyCycle(detail.cycleId);
      setSelectedId(null);
      setDetail(null);
      await loadList();
    } catch {
      reportError(setError, '삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="contract-panel qpolicy-panel">
      <aside className="contract-panel__sidebar">
        <div className="contract-panel__side-header">
          <h2>품질방침</h2>
          <button
            type="button"
            className="ncr-btn is-primary"
            onClick={() => setShowCreate((v) => !v)}
          >
            {showCreate ? '취소' : '연도 추가'}
          </button>
        </div>

        {showCreate && (
          <form className="qpolicy-create" onSubmit={handleCreate}>
            <label>
              연도
              <input
                type="number"
                min={2000}
                max={2100}
                value={createYear}
                onChange={(e) => setCreateYear(e.target.value)}
                required
              />
            </label>
            <label>
              제목 (선택)
              <input
                type="text"
                value={createTitle}
                onChange={(e) => setCreateTitle(e.target.value)}
                placeholder="예: 2026년 품질방침"
              />
            </label>
            <button type="submit" className="ncr-btn is-primary" disabled={saving}>
              생성
            </button>
          </form>
        )}

        <div className="contract-panel__list-wrap">
          {loading ? (
            <p className="contract-panel__empty">불러오는 중...</p>
          ) : list.length === 0 ? (
            <p className="contract-panel__empty">등록된 연도가 없습니다.</p>
          ) : (
            <ul className="contract-panel__list">
              {list.map((row) => (
                <li key={row.cycleId}>
                  <button
                    type="button"
                    className={`contract-panel__list-item${
                      selectedId === row.cycleId ? ' is-active' : ''
                    }`}
                    onClick={() => {
                      setSelectedId(row.cycleId);
                      setActiveDoc('plan');
                      setApprovalMessage('');
                      setApprovalOpen(false);
                    }}
                  >
                    <strong>{row.cycleYear}년</strong>
                    <span className={`qpolicy-badge ${statusClass(row.status)}`}>
                      {statusLabel(row.status)}
                    </span>
                    <span className="qpolicy-list-meta">
                      계획 {statusLabel(row.planStatus, row.planApprovalInProgress)} · 세부{' '}
                      {statusLabel(row.detailStatus, row.detailApprovalInProgress)} · 보고{' '}
                      {statusLabel(row.reportStatus, row.reportApprovalInProgress)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      <section className="contract-panel__content">
        {error && <p className="qpolicy-error">{error}</p>}
        {approvalMessage && <p className="qpolicy-hint">{approvalMessage}</p>}

        {!detail ? (
          <div className="contract-panel__empty-state">
            <p>왼쪽에서 연도를 선택하거나 새 연도를 추가하세요.</p>
            <p className="qpolicy-hint">
              품질방침은 연도별로 추진 계획서 → 세부 계획서 → 보고서 순으로 관리합니다.
            </p>
          </div>
        ) : (
          <>
            <div className="contract-panel__toolbar">
              <div>
                <h1>
                  {detail.cycleYear}년 {detail.title || '품질방침'}
                </h1>
                <span className={`qpolicy-badge ${statusClass(detail.status)}`}>
                  {statusLabel(detail.status)}
                </span>
              </div>
              <div className="ncr-actions">
                {locked ? (
                  <button
                    type="button"
                    className="ncr-btn is-secondary"
                    disabled={saving}
                    onClick={handleReopenCycle}
                  >
                    재개
                  </button>
                ) : canCloseCycle ? (
                  <button
                    type="button"
                    className="ncr-btn is-secondary"
                    disabled={saving}
                    onClick={handleCloseCycle}
                  >
                    마감
                  </button>
                ) : null}
                <button
                  type="button"
                  className="ncr-btn is-danger"
                  disabled={saving}
                  onClick={handleDelete}
                >
                  삭제
                </button>
              </div>
            </div>

            <div className="qpolicy-stages">
              {stageCards.map((card) => (
                <button
                  key={card.key}
                  type="button"
                  className={`qpolicy-stage${activeDoc === card.key ? ' is-active' : ''}`}
                  onClick={() => {
                    setActiveDoc(card.key);
                    setApprovalMessage('');
                    setApprovalOpen(false);
                  }}
                >
                  <span className="qpolicy-stage__step">{card.step}</span>
                  <strong>{card.label}</strong>
                  <span className={`qpolicy-badge ${statusClass(card.status, card.approvalInProgress)}`}>
                    {statusLabel(card.status, card.approvalInProgress)}
                  </span>
                  <em>{card.title || '제목 없음'}</em>
                </button>
              ))}
            </div>

            <div className="qpolicy-editor">
              <div className="qpolicy-editor__header">
                <div>
                  <h2>{DOC_META[activeDoc].label}</h2>
                  {locked && <span className="qpolicy-locked">마감된 연도 — 읽기 전용</span>}
                  {!locked && sectionApproved && (
                    <span className="qpolicy-locked">승인 완료 — 읽기 전용</span>
                  )}
                  {!locked && !sectionApproved && sectionApprovalPending.approvalInProgress && (
                    <span className="qpolicy-locked">결재 요청 중 — 취소 후 수정 가능</span>
                  )}
                  <span className="qpolicy-current-status">
                    현재: {statusLabel(currentDocStatus, sectionApprovalPending.approvalInProgress)}
                  </span>
                </div>
                <div className="qpolicy-editor__header-right">
                  <StampBox
                    authorName={sectionStamps.authorName}
                    reviewerName={sectionStamps.reviewerName}
                    approverName={sectionStamps.approverName}
                  />
                  <div id="qpolicy-doc-actions" className="ncr-actions qpolicy-editor__actions">
                    {sectionApproved ? (
                      <button
                        type="button"
                        className="ncr-btn is-primary"
                        disabled={pdfDownloading}
                        onClick={() => void handleDownloadPdf()}
                      >
                        {pdfDownloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
                      </button>
                    ) : null}
                    {sectionCanRevoke ? (
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        disabled={approving || saving}
                        onClick={() => void handleRevoke()}
                      >
                        {approving ? '철회 중...' : '승인 철회'}
                      </button>
                    ) : null}
                    <ApprovalCancelButton
                      detail={sectionApprovalPending}
                      className="ncr-btn is-secondary"
                      disabled={approving || saving}
                      onCancelled={async () => {
                        if (!detail) return;
                        const refreshed = await fetchQualityPolicyCycle(detail.cycleId);
                        setDetail(refreshed);
                        await loadList();
                      }}
                    />
                    {!editLocked ? (
                      <button
                        type="button"
                        className="ncr-btn is-primary"
                        disabled={saving || approving}
                        onClick={() => void handleSave()}
                      >
                        {saving ? '저장 중...' : '저장'}
                      </button>
                    ) : null}
                    {canRequestApproval ? (
                      <>
                        <button
                          type="button"
                          className="ncr-btn is-primary"
                          disabled={approving || saving}
                          onClick={() => {
                            setApprovalMessage('');
                            setApprovalOpen(true);
                          }}
                        >
                          결재 요청
                        </button>
                        {canApproveDirect ? (
                          <button
                            type="button"
                            className="ncr-btn is-approve"
                            disabled={approving || saving}
                            onClick={() => void handleApprove()}
                          >
                            {approving ? '승인 중...' : '승인'}
                          </button>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
              {sectionApprovalPending.approvalInProgress ? (
                <p className="qpolicy-hint">
                  결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.
                </p>
              ) : null}

              <label className="qpolicy-field">
                문서 제목
                <input
                  type="text"
                  value={docTitle}
                  onChange={(e) => setDocTitle(e.target.value)}
                  disabled={editLocked || saving}
                  placeholder={`${DOC_META[activeDoc].label} 제목`}
                />
              </label>

              {activeDoc === 'plan' && (
                <QualityPlanFormView
                  year={detail.cycleYear}
                  value={planForm}
                  disabled={editLocked || saving}
                  onChange={setPlanForm}
                />
              )}
              {activeDoc === 'detail' && (
                <QualityDetailFormView
                  year={detail.cycleYear}
                  value={detailForm}
                  disabled={editLocked || saving}
                  onChange={setDetailForm}
                  onSyncFromPlan={() =>
                    setDetailForm((prev) => syncTasksFromObjectives(prev, planForm.objectives))
                  }
                />
              )}
              {activeDoc === 'report' && (
                <QualityReportFormView
                  year={detail.cycleYear}
                  value={reportForm}
                  disabled={editLocked || saving}
                  onChange={setReportForm}
                  onSyncFromPlan={() =>
                    setReportForm((prev) => syncResultsFromObjectives(prev, planForm.objectives))
                  }
                />
              )}

              {!canRequestApproval && !editLocked && currentDocStatus === 'EMPTY' ? (
                <p className="qpolicy-hint">
                  내용을 작성한 뒤 <strong>저장</strong>하면 결재 요청·승인 버튼이 나타납니다.
                </p>
              ) : null}

              <div className="ncr-actions qpolicy-editor__actions" style={{ marginTop: 16 }}>
                {sectionApproved ? (
                  <button
                    type="button"
                    className="ncr-btn is-primary"
                    disabled={pdfDownloading}
                    onClick={() => void handleDownloadPdf()}
                  >
                    {pdfDownloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
                  </button>
                ) : null}
                {sectionCanRevoke ? (
                  <button
                    type="button"
                    className="ncr-btn is-danger"
                    disabled={approving || saving}
                    onClick={() => void handleRevoke()}
                  >
                    {approving ? '철회 중...' : '승인 철회'}
                  </button>
                ) : null}
                <ApprovalCancelButton
                  detail={sectionApprovalPending}
                  className="ncr-btn is-secondary"
                  disabled={approving || saving}
                  onCancelled={async () => {
                    if (!detail) return;
                    const refreshed = await fetchQualityPolicyCycle(detail.cycleId);
                    setDetail(refreshed);
                    await loadList();
                  }}
                />
                {!editLocked ? (
                  <button
                    type="button"
                    className="ncr-btn is-primary"
                    disabled={saving || approving}
                    onClick={() => void handleSave()}
                  >
                    {saving ? '저장 중...' : '저장'}
                  </button>
                ) : null}
                {canRequestApproval ? (
                  <>
                    <button
                      type="button"
                      className="ncr-btn is-primary"
                      disabled={approving || saving}
                      onClick={() => {
                        setApprovalMessage('');
                        setApprovalOpen(true);
                      }}
                    >
                      결재 요청
                    </button>
                    {canApproveDirect ? (
                      <button
                        type="button"
                        className="ncr-btn is-approve"
                        disabled={approving || saving}
                        onClick={() => void handleApprove()}
                      >
                        {approving ? '승인 중...' : '승인'}
                      </button>
                    ) : null}
                  </>
                ) : null}
              </div>
            </div>

            <ApprovalRequestModal
              open={approvalOpen}
              titleDefault={`${docTitle || defaultTitle(detail.cycleYear, activeDoc)} 결재`}
              targetType={DOC_APPROVAL_TARGET[activeDoc]}
              targetId={detail.cycleId}
              onClose={() => setApprovalOpen(false)}
              onSubmitted={() => {
                setApprovalOpen(false);
                setApprovalMessage(
                  '결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.'
                );
                void fetchQualityPolicyCycle(detail.cycleId).then(setDetail);
                void loadList();
              }}
            />
          </>
        )}
      </section>
    </main>
  );
}

export default QualityPolicyPanel;
